import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import {
  AccountBalance,
  AccountBalanceWallet,
  CurrencyBitcoin,
  ShowChart,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useOutletContext, useParams } from 'react-router-dom';

import {
  getContributions,
  getDividendsProjection,
  getDividendsYearly,
  getPortfolio,
  getPortfolioAverageBalances,
  getPortfolioReturns,
  getTransactions,
  putPosition,
} from '../../http/PortfoliosApi';
import { getWalletBalancesByToken, getWallets } from '../../http/WalletsApi';
import { getCompanies } from '../../http/CompaniesApi';
import { getTokens } from '../../http/TokensApi';
import AddPortfolioDialog from './dialogs/AddPortfolioDialog';

import { ClockIcon } from '@mui/x-date-pickers';
import PortfolioAnalyticsComponent from './tabs/analytics/PortfolioAnalyticsComponent';
import PortfolioManagementComponent from './tabs/management/PortfolioManagementComponent';
import PortfolioStatusComponent from './tabs/status/PortfolioStatusComponent';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DEFAULT_ERROR_MESSAGE = 'An error happened!';

function StatCard({ icon, label, value, gradient, shadowRgb }) {
  return (
    <Card
      sx={{
        height: '100%',
        background: gradient,
        color: '#ffffff',
        boxShadow: `0 4px 12px rgba(${shadowRgb}, 0.15)`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: `0 6px 16px rgba(${shadowRgb}, 0.25)`,
        },
      }}
    >
      <CardContent
        sx={{
          p: SPACING.MD,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: SPACING.XS,
        }}
      >
        {icon}
        <Typography
          color="rgba(255,255,255,0.8)"
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function PortfolioComponent() {
  const [addPortfolioDialogOpen, setAddPortfolioDialogOpen] =
    useOutletContext();
  const { id } = useParams();
  const [portfolio, setPortfolio] = useState({});
  const [positions, setPositions] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [contributions, setContributions] = useState({});
  const [transactions, setTransactions] = useState({});
  const [portfolioMetrics, setPortfolioMetrics] = useState({});
  const [metricsRange, setMetricsRange] = useState('sixMonths');
  const [value, setValue] = useState(0);
  const [contributionsPage, setContributionsPage] = useState(0);
  const [snackState, setSnackState] = useState({
    open: false,
    message: DEFAULT_ERROR_MESSAGE,
  });
  const [meta, setMeta] = useState({
    sumWeights: 100,
    isValid: true,
    totalValueEUR: 0,
  });
  const [wallets, setWallets] = useState([]);
  const [tokenBalances, setTokenBalances] = useState([]);
  const [dividends, setDividends] = useState({ projection: null, yearly: [] });

  const amountFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  });

  const walletsTotalEUR = useMemo(
    () =>
      wallets.reduce((sum, walletEntry) => {
        const walletValue = Number(walletEntry?.fiatValue || 0);
        return sum + (Number.isFinite(walletValue) ? walletValue : 0);
      }, 0),
    [wallets],
  );

  const handleSnackStateChange = useCallback((key, value) => {
    setSnackState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const loadPortfolio = useCallback(async () => {
    const { data } = await getPortfolio(id);
    const { data: contributionsData } = await getContributions(id);
    const { data: transactionsData } = await getTransactions(id);
    const { data: walletsData } = await getWallets(id);
    setPortfolio(data);
    setContributions(contributionsData);
    setTransactions(transactionsData);

    // Positions don't carry the company `iconUrl`, so join it in by symbol
    // from /companies. Best-effort: if the call fails, positions still render
    // (AssetIcon falls back to initials).
    let iconBySymbol = new Map();
    try {
      const { data: companiesData } = await getCompanies();
      iconBySymbol = new Map(
        (companiesData || []).map((c) => [c.symbol, c.iconUrl]),
      );
    } catch {
      // ignore — logos are optional
    }

    setPositions(
      (data &&
        data.positions &&
        data.positions.map((p) => ({
          ...p,
          id: p.id,
          iconUrl: p.iconUrl ?? iconBySymbol.get(p.symbol),
        }))) ||
        [],
    );

    setMeta({
      sumWeights: data.state.sumWeights,
      isValid: data.state.isValid,
      totalValueEUR: Number(data.state.totalValueEUR),
    });

    // Wallet balances don't carry the token `iconUrl` either, so join it in
    // from /tokens (by id, falling back to address). Best-effort as above.
    const tokenIcon = new Map();
    try {
      const { data: tokensData } = await getTokens();
      (tokensData || []).forEach((t) => {
        if (t.id != null) tokenIcon.set(`id:${t.id}`, t.iconUrl);
        if (t.address)
          tokenIcon.set(`addr:${t.address.toLowerCase()}`, t.iconUrl);
      });
    } catch {
      // ignore — logos are optional
    }

    const resolveTokenIcon = (token) =>
      token?.iconUrl ??
      tokenIcon.get(`id:${token?.id}`) ??
      tokenIcon.get(`addr:${token?.address?.toLowerCase()}`);

    setWallets(
      (walletsData || []).map((entry) => ({
        ...entry,
        balances: (entry.balances || []).map((b) => ({
          ...b,
          token: b.token && { ...b.token, iconUrl: resolveTokenIcon(b.token) },
        })),
      })),
    );

    // Same balances aggregated across every wallet, one row per token. Already
    // sorted by fiatValue desc by the API. Best-effort: the per-wallet cards
    // still render if this call fails.
    try {
      const { data: tokenBalancesData } = await getWalletBalancesByToken(id);
      setTokenBalances(
        (tokenBalancesData || []).map((row) => ({
          ...row,
          token: row.token && {
            ...row.token,
            iconUrl: resolveTokenIcon(row.token),
          },
        })),
      );
    } catch {
      setTokenBalances([]);
    }

    // Reloaded with the portfolio because the projection depends on the
    // current shares. Best-effort: the Analytics tab shows an empty state.
    try {
      const [{ data: projection }, { data: yearly }] = await Promise.all([
        getDividendsProjection(id),
        getDividendsYearly(id),
      ]);
      setDividends({ projection, yearly: yearly || [] });
    } catch {
      setDividends({ projection: null, yearly: [] });
    }
  }, [id]);

  const reloadContributions = useCallback(
    async (offset, limit) => {
      const { data: contributionsData } = await getContributions(
        id,
        offset,
        limit,
      );
      setContributions(contributionsData);
    },
    [id],
  );

  const reloadTransactions = useCallback(
    async (offset, limit, filter = {}) => {
      const { data: transactionsData } = await getTransactions(
        id,
        offset,
        limit,
        filter,
      );
      setTransactions(transactionsData);
    },
    [id],
  );

  const loadPortfolioMetrics = useCallback(async () => {
    const { data } = await getPortfolioAverageBalances(id, metricsRange);
    const { data: returns } = await getPortfolioReturns(id, metricsRange);
    setPortfolioMetrics((prev) => ({
      ...prev,
      averageBalances: data,
      returns,
    }));
  }, [id, metricsRange]);
  useEffect(() => {
    if (id) {
      loadPortfolio();
      loadPortfolioMetrics();
    }
  }, [id, metricsRange, loadPortfolio, loadPortfolioMetrics]);

  const updateRow = useCallback(
    async (newValue, oldValue) => {
      try {
        const { symbol, targetWeight, shares } = newValue;
        await putPosition(portfolio.id, {
          symbol,
          targetWeight,
          shares,
        });
        await loadPortfolio();
        return newValue;
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
        return oldValue;
      }
    },
    [portfolio.id, loadPortfolio, handleSnackStateChange],
  );

  const getPortfolioStateTimestamp = useCallback(() => {
    const timestamp = portfolio?.state?.timestamp;
    return timestamp ? format(new Date(timestamp), 'dd/MM p') : '';
  }, [portfolio?.state?.timestamp]);

  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);
  }, []);

  const TabPanel = useMemo(
    () =>
      ({ children, value, index }) => {
        return (
          <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
          >
            {value === index && <Box>{children}</Box>}
          </div>
        );
      },
    [],
  );

  const summaryRows = [
    {
      label: 'Total bolsa',
      value: amountFormatter.format(meta.totalValueEUR),
      dotColor: '#1976d2',
    },
    {
      label: 'Total cripto',
      value: amountFormatter.format(walletsTotalEUR),
      dotColor: '#d97706',
    },
    {
      label: 'Total',
      value: amountFormatter.format(meta.totalValueEUR + walletsTotalEUR),
      dotColor: '#4338ca',
    },
    { label: 'Last update', value: getPortfolioStateTimestamp() },
    { label: 'Allocation', value: `${meta.sumWeights}%` },
    { label: 'Wallets', value: wallets.length },
  ];

  return (
    <Box sx={{ display: 'flex', alignSelf: 'center' }}>
      <Grid container spacing={SPACING.LG} sx={{ p: SPACING.LG }}>
        {/* Portfolio Info Cards */}
        <Grid item xs={12}>
          {isMobile ? (
            <Card sx={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {summaryRows.map((row, idx) => (
                  <Box key={row.label}>
                    {idx > 0 && <Divider />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: SPACING.SM,
                        py: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {row.dotColor && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: row.dotColor,
                            }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {row.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={SPACING.SM}>
              {/* Total Bolsa */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={<ShowChart sx={{ fontSize: 20 }} />}
                  label="Total bolsa"
                  value={amountFormatter.format(meta.totalValueEUR)}
                  gradient="linear-gradient(135deg, #2196f3 0%, #1976d2 100%)"
                  shadowRgb="33, 150, 243"
                />
              </Grid>

              {/* Total Cripto */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={<CurrencyBitcoin sx={{ fontSize: 20 }} />}
                  label="Total cripto"
                  value={amountFormatter.format(walletsTotalEUR)}
                  gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                  shadowRgb="245, 158, 11"
                />
              </Grid>

              {/* Total */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={<AccountBalance sx={{ fontSize: 20 }} />}
                  label="Total"
                  value={amountFormatter.format(
                    meta.totalValueEUR + walletsTotalEUR,
                  )}
                  gradient="linear-gradient(135deg, #6366f1 0%, #4338ca 100%)"
                  shadowRgb="99, 102, 241"
                />
              </Grid>

              {/* Last Update */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={<ClockIcon sx={{ fontSize: 20 }} />}
                  label="Last update"
                  value={getPortfolioStateTimestamp()}
                  gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  shadowRgb="102, 126, 234"
                />
              </Grid>

              {/* Allocation */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={
                    meta.isValid ? (
                      <CheckCircleIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <ClearIcon sx={{ fontSize: 20 }} />
                    )
                  }
                  label="Allocation"
                  value={`${meta.sumWeights}%`}
                  gradient={
                    meta.isValid
                      ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                      : 'linear-gradient(135deg, #f44336 0%, #da190b 100%)'
                  }
                  shadowRgb={meta.isValid ? '76, 175, 80' : '244, 67, 54'}
                />
              </Grid>

              {/* Wallets */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  icon={<AccountBalanceWallet sx={{ fontSize: 20 }} />}
                  label="Wallets"
                  value={wallets.length}
                  gradient="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)"
                  shadowRgb="20, 184, 166"
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Tabs Section */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange}>
                <Tab label="Status" />
                <Tab label="Analytics" />
                <Tab label="Management" />
              </Tabs>
            </Box>
            <Box sx={{ p: SPACING.MD, px: isMobile ? 0 : SPACING.MD }}>
              <TabPanel value={value} index={0}>
                <PortfolioStatusComponent
                  positions={positions}
                  updateRow={updateRow}
                  wallets={wallets}
                  tokenBalances={tokenBalances}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <PortfolioAnalyticsComponent
                  setMetricsRange={setMetricsRange}
                  portfolioMetrics={portfolioMetrics}
                  contributions={contributions}
                  portfolio={portfolio}
                  metricsRange={metricsRange}
                  dividends={dividends}
                  stocksTotalEUR={meta.totalValueEUR}
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <PortfolioManagementComponent
                  contributions={contributions}
                  loadPortfolio={loadPortfolio}
                  page={contributionsPage}
                  portfolio={portfolio}
                  portfolios={portfolios}
                  setPortfolios={setPortfolios}
                  reloadContributions={reloadContributions}
                  setPage={setContributionsPage}
                  transactions={transactions}
                  reloadTransactions={reloadTransactions}
                />
              </TabPanel>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <AddPortfolioDialog
        setPortfolios={setPortfolios}
        addPortfolioDialogOpen={addPortfolioDialogOpen}
        setAddPortfolioDialogOpen={setAddPortfolioDialogOpen}
        portfolios={portfolios}
        errMessage={snackState.message}
        setErrMessage={(msg) => handleSnackStateChange('message', msg)}
        setSnackOpen={(open) => handleSnackStateChange('open', open)}
      />
      <Snackbar
        open={snackState.open}
        autoHideDuration={6000}
        onClose={() => handleSnackStateChange('open', false)}
      >
        <Alert severity="error">{snackState.message}</Alert>
      </Snackbar>
    </Box>
  );
}
